import React, { useEffect, useMemo, useState } from "react";
import { api, exportCsv, formatApiError, fmtQty, todayStr, monthStartStr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { StockFilters, cleanParams } from "@/components/stock/StockFilters";
import { DataTable } from "@/components/common/DataTable";
import { KindBadge } from "@/components/common/cells";
import { toast } from "sonner";

export const StockSummaryTab = () => {
  const { t } = useLang();
  const [filters, setFilters] = useState({ start: monthStartStr(), end: todayStr(), item_type: "", warehouse: "" });
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!filters.start || !filters.end) return;
    api.get("/reports/stock-summary", { params: cleanParams(filters) })
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [filters]);

  const columns = useMemo(() => [
    { key: "kind", label: t("kind"), value: (r) => String(r.kind).toUpperCase(), render: (r) => <KindBadge kind={r.kind} /> },
    { key: "code", label: t("code"), className: "font-mono text-xs" },
    { key: "name", label: t("name"), className: "font-medium" },
    { key: "unit", label: t("unit"), className: "text-slate-500" },
    { key: "opening", label: t("opening_stock"), align: "right", className: "font-mono", render: (r) => fmtQty(r.opening) },
    { key: "in_qty", label: t("in_qty"), align: "right", className: "font-mono text-emerald-700", render: (r) => (r.in_qty ? `+${fmtQty(r.in_qty)}` : "0") },
    { key: "out_qty", label: t("out_qty"), align: "right", className: "font-mono text-red-700", render: (r) => (r.out_qty ? `−${fmtQty(r.out_qty)}` : "0") },
    { key: "closing", label: t("closing_stock"), align: "right", className: "font-mono font-bold text-slate-900", render: (r) => fmtQty(r.closing) },
  ], [t]);

  return (
    <Card className="border-slate-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-slate-200 flex flex-wrap gap-4 items-end justify-between">
          <StockFilters value={filters} onChange={setFilters} showWarehouse warehouseAllLabel={t("all_available")} testid="summary-filter" />
          <Button size="sm" variant="outline" onClick={() => exportCsv("stock-summary", cleanParams(filters))} data-testid="summary-export-btn">
            <Download size={14} className="mr-2" />{t("export_excel")}
          </Button>
        </div>
        <div className={`px-4 py-2 text-xs border-b border-slate-200 ${filters.warehouse === "reject" ? "bg-rose-50 text-rose-800" : "bg-slate-50 text-slate-500"}`} data-testid="summary-scope-note">
          {filters.warehouse === "reject" ? t("reject_note") : filters.warehouse ? `${t("warehouse")}: ${t(`wh_${filters.warehouse}`)}` : t("summary_all_note")}
        </div>
        <DataTable columns={columns} rows={rows} rowKey={(r) => `${r.kind}-${r.id}`} testid="stock-summary-table" />
      </CardContent>
    </Card>
  );
};
